import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Edulead } from 'edulead';
import { FeedList } from '../custom-components/FeedList';
import LoadingBars from '../custom-components/LoadingBars';
import { GlobalContext } from '../global-context';
import { eduleadV4 } from '../services/edulead-v4-client';
import { ActivityFeedItem, subscribeToActivityFeed } from '../services/pusher.service';
import { toActivityFeedItem } from '../utils/feedItem';
import General from '../utils/GeneralUtils';
import './Feed.scss';

const ANIMATION_DURATION = 400;
const ITEM_DELAY = 500; // Time between each item appearing
const MAX_FEED_ITEMS = 500;
const INITIAL_LOAD = 50;
const LOAD_MORE_BATCH = 50;

const feedItem = (item: Edulead.EduleadV4FeedItem) =>
  toActivityFeedItem(item, item.person_id ?? '', item.deliver_time ?? '');

export function Feed() {
  const globalContext = useContext(GlobalContext);
  const [feedItems, setFeedItems] = useState<ActivityFeedItem[]>([]);
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [nextPageToken, setNextPageToken] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const itemQueueRef = useRef<ActivityFeedItem[]>([]);
  const isProcessingRef = useRef(false);

  General.setTitle(document, 'Feed', globalContext);

  const processQueue = useCallback(() => {
    if (isProcessingRef.current || itemQueueRef.current.length === 0) {
      return;
    }

    isProcessingRef.current = true;
    const item = itemQueueRef.current.shift()!;

    setFeedItems((prev) => {
      if (prev.some((existing) => existing.id === item.id)) {
        return prev;
      }
      const updated = [item, ...prev];
      // Evict oldest if over limit
      if (updated.length > MAX_FEED_ITEMS) {
        return updated.slice(0, MAX_FEED_ITEMS);
      }
      return updated;
    });
    setNewItemIds((prev) => new Set(prev).add(item.id));

    // Clear the "new" flag after animation completes
    setTimeout(() => {
      setNewItemIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }, ANIMATION_DURATION);

    // Process next item after delay
    setTimeout(() => {
      isProcessingRef.current = false;
      processQueue();
    }, ITEM_DELAY);
  }, []);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const page = await eduleadV4.feedItems.list({ page_size: INITIAL_LOAD });
      setFeedItems(page.data.map(feedItem));
      setNextPageToken(page.response.next_page_token ?? '');
      setNewItemIds(new Set());
      itemQueueRef.current = [];
      isProcessingRef.current = false;
    } catch (err) {
      console.error('[Feed] Failed to fetch items:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextPageToken || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const page = await eduleadV4.feedItems.list({
        page_size: LOAD_MORE_BATCH,
        page_token: nextPageToken,
      });
      setFeedItems((prev) => {
        const combined = [...prev, ...page.data.map(feedItem)];
        // Cap at MAX_FEED_ITEMS (keep newest)
        return combined.slice(0, MAX_FEED_ITEMS);
      });
      setNextPageToken(page.response.next_page_token ?? '');
    } catch (err) {
      console.error('[Feed] Failed to load more:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [nextPageToken, isLoadingMore]);

  useEffect(() => {
    loadItems();

    const unsubscribe = subscribeToActivityFeed({
      onNewItem: (item) => {
        itemQueueRef.current.push(item);
        processQueue();
      },
    });

    return () => unsubscribe();
  }, [loadItems, processQueue]);

  return (
    <div className="feed">
      <div className="feed__content">
        <div className="feed__content-inner">
          {isLoading ? (
            <LoadingBars style={{ padding: '14px 20px' }} numberOfRows={10} barHeight={60} />
          ) : (
            <FeedList
              items={feedItems}
              newItemIds={newItemIds}
              hasMore={nextPageToken !== ''}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
            />
          )}
        </div>
      </div>
    </div>
  );
}
